import type Alpine from 'alpinejs';
import {
    findAlpineMachineOwner,
} from '../alpine-machine';
import {
    findClosestDomInvalidationOwners,
    type DomInvalidationOwner,
} from '../dom-invalidation';
import {stableIdentity} from '../identity';
import {declareMorphAttributes, preserveDeclaredMorphAttributes} from '../morph';
import {isRecord, warn} from '../../utils';
import {loadAssets, type AssetDescriptor} from '../../assets/bridge';
import type {RequestLifecycleDetail} from './request';
import {replaceFragmentChildrenNow} from './fragment';

type PatchMode = 'morph' | 'outer' | 'replace' | 'update' | 'append' | 'prepend' | 'before' | 'after' | 'remove';

type StreamPatchPayload = {
    html?: string | null;
    target?: string | null;
    selector?: string | null;
    mode?: PatchMode | null;
    requestId?: string | null;
    viewTransition?: boolean;
    assets?: AssetDescriptor[];
};

type StreamEventPayload = {
    event: string;
    detail?: Record<string, unknown>;
};

type UiEffectPayload =
    | ({type: 'patch'} & StreamPatchPayload)
    | ({type: 'dispatch'} & StreamEventPayload);

type SseFrame = {
    event: string;
    data: string;
};

type StreamController = {
    abort(reason?: string): void;
};

type StreamLifecycle = {
    lastEventId?: string;
    open?(): void;
    disconnected?(): void;
    eventId?(value: string): void;
    reset?(data: string): void;
    error?(data: string): void;
};
type StreamPatchAnimator = (
    AlpineInstance: StreamAlpine,
    patches: StreamPatchPayload[],
    update: () => void,
) => void;

type MorphOptions = {
    key?(el: HTMLElement): string | null;
    updating?(
        from: Node,
        to: Node,
        childrenOnly?: () => void,
        skip?: () => void,
        skipChildren?: () => void,
        skipUntil?: (predicate: (node: Node) => boolean) => void,
    ): void;
    updated?(from: Node, to: Node): void;
};

type StreamAlpine = Pick<Alpine.Alpine, 'initTree'> & Partial<Pick<Alpine.Alpine, 'findClosest'>> & {
    morph?: (
        target: HTMLElement,
        html: string,
        options?: MorphOptions,
    ) => void;
};

const pendingDomInvalidations = new Set<DomInvalidationOwner>();
let domInvalidationQueued = false;
let effectRequestFilter: ((requestId: string | null) => boolean) | null = null;
let morphExclusionFilter: ((requestId: string | null, element: Element) => boolean) | null = null;
let streamPatchAnimator: StreamPatchAnimator | null = null;
let patchQueue = Promise.resolve();
let queuedPatches = 0;

const queueDomInvalidation = (owners: Iterable<DomInvalidationOwner>): void => {
    for (const owner of owners) pendingDomInvalidations.add(owner);
    if (domInvalidationQueued || pendingDomInvalidations.size === 0) return;
    domInvalidationQueued = true;
    queueMicrotask(() => {
        domInvalidationQueued = false;
        const queued = Array.from(pendingDomInvalidations);
        pendingDomInvalidations.clear();
        queued.forEach((owner) => owner.invalidateDom());
    });
};

const parseHtml = (html: string): HTMLTemplateElement => {
    const template = document.createElement('template');
    template.innerHTML = html;
    return template;
};

const elementsFromHtml = (html: string): HTMLElement[] => (
    Array.from(parseHtml(html).content.children)
        .filter((child): child is HTMLElement => child instanceof HTMLElement)
);

const initInserted = (AlpineInstance: StreamAlpine, elements: HTMLElement[]): void => {
    elements.forEach((element) => AlpineInstance.initTree(element));
};

const resolvedMode = (target: Element, payloadMode?: PatchMode | null): PatchMode => {
    if (payloadMode) return payloadMode;
    const merge = target.getAttribute('x-merge');
    if (merge) return merge as PatchMode;
    return 'replace';
};

const targetFromPayload = (payload: StreamPatchPayload): Element | null => {
    if (payload.selector) return document.querySelector(payload.selector);
    if (!payload.target) return null;

    const id = payload.target.startsWith('#') ? payload.target.slice(1) : payload.target;
    return document.getElementById(id);
};

const applyPatchToTarget = (
    AlpineInstance: StreamAlpine,
    target: Element,
    payload: StreamPatchPayload,
): void => {
    const mode = resolvedMode(target, payload.mode);

    if (mode === 'remove') {
        target.remove();
        return;
    }

    if (!payload.html) {
        warn('x-ui-stream: received patch with no html');
        return;
    }

    if (mode === 'morph' || mode === 'outer') {
        if (typeof AlpineInstance.morph === 'function') {
            const affectedOwners = new Set<DomInvalidationOwner>();
            AlpineInstance.morph(target as HTMLElement, payload.html, {
                key(el: HTMLElement): string | null {
                    return stableIdentity(el);
                },
                updating(from: Node, to: Node, _childrenOnly?: () => void, skip?: () => void): void {
                    if (
                        from instanceof Element
                        && morphExclusionFilter?.(payload.requestId ?? null, from)
                    ) {
                        skip?.();
                        return;
                    }
                    preserveDeclaredMorphAttributes(from, to);
                },
                updated(from: Node): void {
                    if (!(from instanceof Element)) return;
                    const owner = findAlpineMachineOwner(AlpineInstance as Alpine.Alpine, from);
                    if (owner) affectedOwners.add(owner);
                    findClosestDomInvalidationOwners(from).forEach((candidate) => {
                        affectedOwners.add(candidate);
                    });
                },
            });
            queueDomInvalidation(affectedOwners);
            return;
        }

        warn('x-ui-stream: morph mode requires an Alpine morph implementation; falling back to replace');
    }

    if (mode === 'replace' || mode === 'morph' || mode === 'outer') {
        const [newEl] = elementsFromHtml(payload.html);
        if (!newEl) {
            warn('x-ui-stream: replace mode produced no element');
            return;
        }
        target.replaceWith(newEl);
        AlpineInstance.initTree(newEl);
        return;
    }

    if (mode === 'update') {
        replaceFragmentChildrenNow(AlpineInstance, target, payload.html);
        return;
    }

    const template = parseHtml(payload.html);
    const inserted = Array.from(template.content.children)
        .filter((child): child is HTMLElement => child instanceof HTMLElement);

    if (mode === 'append') {
        target.append(template.content);
        initInserted(AlpineInstance, inserted);
        return;
    }

    if (mode === 'prepend') {
        target.prepend(template.content);
        initInserted(AlpineInstance, inserted);
        return;
    }

    if (mode === 'before') {
        target.before(template.content);
        initInserted(AlpineInstance, inserted);
        return;
    }

    if (mode === 'after') {
        target.after(template.content);
        initInserted(AlpineInstance, inserted);
    }
};

const applyStreamPatchNow = (AlpineInstance: StreamAlpine, payload: StreamPatchPayload): void => {
    const target = targetFromPayload(payload);

    if (target) {
        applyPatchToTarget(AlpineInstance, target, payload);
        return;
    }

    if (payload.selector || payload.target) {
        warn('x-ui-stream: target not found', payload.selector ?? payload.target);
        return;
    }

    if (!payload.html) {
        warn('x-ui-stream: received patch with no html');
        return;
    }

    elementsFromHtml(payload.html).forEach((child) => {
        if (!child.id) {
            warn('x-ui-stream: top-level element has no id, skipping', child);
            return;
        }

        const inferredTarget = document.getElementById(child.id);
        if (!inferredTarget) return;
        applyPatchToTarget(AlpineInstance, inferredTarget, {...payload, html: child.outerHTML});
    });
};

const applyStreamPatches = (AlpineInstance: StreamAlpine, payload: StreamPatchPayload | StreamPatchPayload[]): void => {
    const patches = Array.isArray(payload) ? payload : [payload];
    const apply = (): void => {
        patches.forEach((patch) => applyStreamPatchNow(AlpineInstance, patch));
    };

    const applyWithAnimation = (): void => {
        const animatedPatches = patches.filter((patch) => patch.viewTransition === true);
        if (animatedPatches.length === 0) {
            apply();
            return;
        }
        if (streamPatchAnimator) streamPatchAnimator(AlpineInstance, animatedPatches, apply);
        else apply();
    };
    const assets = patches.flatMap((patch) => patch.assets ?? []);

    if (assets.length === 0 && queuedPatches === 0) {
        applyWithAnimation();
        return;
    }

    queuedPatches += 1;
    const run = async (): Promise<void> => {
        if (assets.length > 0) {
            await loadAssets(assets, AlpineInstance as Alpine.Alpine);
        }
        applyWithAnimation();
    };
    const task = patchQueue.then(run, run);
    patchQueue = task
        .catch(() => undefined)
        .finally(() => {
            queuedPatches -= 1;
        });
};

const registerStreamPatchAnimator = (animator: StreamPatchAnimator): void => {
    streamPatchAnimator = animator;
};

const applyStreamPatch = (AlpineInstance: StreamAlpine, payload: StreamPatchPayload): void => {
    applyStreamPatchNow(AlpineInstance, payload);
};

const dispatchStreamEvent = (payload: StreamEventPayload): void => {
    document.dispatchEvent(new CustomEvent(payload.event, {
        detail: payload.detail ?? {},
        bubbles: true,
        composed: true,
    }));
};

const applyUiEffect = (AlpineInstance: StreamAlpine, effect: UiEffectPayload): void => {
    if (effect.type === 'patch') {
        const {type: _type, ...payload} = effect;
        applyStreamPatches(AlpineInstance, payload);
        return;
    }

    if (effect.type === 'dispatch') {
        const {type: _type, ...payload} = effect;
        dispatchStreamEvent(payload);
        return;
    }

    warn('x-ui-stream: unknown UI effect', effect);
};

const applyUiEffects = (AlpineInstance: StreamAlpine, effects: UiEffectPayload | UiEffectPayload[]): void => {
    const events = Array.isArray(effects) ? effects : [effects];

    events.forEach((effect) => applyUiEffect(AlpineInstance, effect));
};

const commandLines = (frame: SseFrame): Map<string, string[]> | null => {
    const commands = new Map<string, string[]>();
    for (const line of frame.data.split('\n')) {
        const space = line.indexOf(' ');
        const hasValue = space > 0;
        const key = hasValue ? line.slice(0, space) : line;

        if (!hasValue && key !== 'html') {
            warn('x-ui-stream: invalid SSE command line', key);
            return null;
        }

        const value = hasValue ? line.slice(space + 1) : '';
        const values = commands.get(key);
        if (values) values.push(value);
        else commands.set(key, [value]);
    }

    return commands;
};

const scalarCommand = (commands: Map<string, string[]>, key: string): string | undefined => {
    const values = commands.get(key);
    if (!values?.length) return undefined;
    if (values.length > 1) warn('x-ui-stream: duplicate SSE command, using last value', key);
    return values.at(-1);
};

const applyPatchFrame = (AlpineInstance: StreamAlpine, frame: SseFrame): void => {
    const commands = commandLines(frame);
    if (!commands || commands.size === 0) return;
    const requestId = scalarCommand(commands, 'request-id');
    if (effectRequestFilter && !effectRequestFilter(requestId ?? null)) return;

    let assets: AssetDescriptor[] | undefined;
    const rawAssets = scalarCommand(commands, 'assets');
    if (rawAssets) {
        try {
            const parsed: unknown = JSON.parse(rawAssets);
            if (!Array.isArray(parsed)) throw new TypeError('assets must be an array');
            assets = parsed as AssetDescriptor[];
        } catch {
            warn('x-ui-stream: invalid patch assets JSON', rawAssets);
            return;
        }
    }

    const payload: StreamPatchPayload = {
        mode: (scalarCommand(commands, 'mode') ?? 'replace') as PatchMode,
        target: scalarCommand(commands, 'target') ?? null,
        selector: scalarCommand(commands, 'selector') ?? null,
        html: commands.get('html')?.join('\n') ?? null,
        requestId: requestId ?? null,
        viewTransition: scalarCommand(commands, 'view-transition') === 'true',
        ...(assets ? {assets} : {}),
    };

    applyStreamPatches(AlpineInstance, payload);
};

const applyDispatchFrame = (frame: SseFrame): void => {
    const commands = commandLines(frame);
    if (!commands) return;
    const requestId = scalarCommand(commands, 'request-id');
    if (effectRequestFilter && !effectRequestFilter(requestId ?? null)) return;
    const event = scalarCommand(commands, 'event');
    if (!event) {
        warn('x-ui-stream: dispatch SSE frame missing event');
        return;
    }

    const rawDetail = scalarCommand(commands, 'detail');
    let detail: Record<string, unknown> = {};
    if (rawDetail) {
        try {
            const parsed: unknown = JSON.parse(rawDetail);
            if (isRecord(parsed)) detail = parsed;
        } catch {
            warn('x-ui-stream: invalid dispatch detail JSON', rawDetail);
            return;
        }
    }

    dispatchStreamEvent({event, detail});
};

const SSE_FRAME_HANDLERS: Record<string, (AlpineInstance: StreamAlpine, frame: SseFrame) => void> = {
    'trellis-ui-patch': applyPatchFrame,
    'trellis-ui-dispatch': (_AlpineInstance, frame) => applyDispatchFrame(frame),
};

const TRELLIS_SSE_EVENT_PREFIX = 'trellis-ui-';

const applySseFrame = (AlpineInstance: StreamAlpine, frame: SseFrame): void => {
    const handler = SSE_FRAME_HANDLERS[frame.event];
    if (!handler) {
        if (frame.event.startsWith(TRELLIS_SSE_EVENT_PREFIX)) {
            warn('x-ui-stream: unsupported SSE event', frame.event);
        }
        return;
    }
    handler(AlpineInstance, frame);
};

const streamUrl = (url: string, scopes = '', client = '', lastEventId = ''): string => {
    const parsed = new URL(url, document.baseURI);
    const normalizedScopes = [...new Set(scopes.split(/\s+/).filter(Boolean))];
    if (client) parsed.searchParams.set('client', client);
    if (lastEventId) parsed.searchParams.set('lastEventId', lastEventId);
    if (normalizedScopes.length > 0) {
        parsed.searchParams.delete('channel');
        normalizedScopes.forEach((scope) => parsed.searchParams.append('channel', scope));
    }

    return /^[a-z][a-z\d+.-]*:/i.test(url) || url.startsWith('//')
        ? parsed.href
        : `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

const connectStream = (
    AlpineInstance: StreamAlpine,
    url: string,
    scopes = '',
    client = '',
    lifecycle: StreamLifecycle = {},
): StreamController => {
    const source = new EventSource(streamUrl(url, scopes, client, lifecycle.lastEventId));
    let active = true;
    const rememberEventId = (event: MessageEvent): void => {
        if (event.lastEventId) lifecycle.eventId?.(event.lastEventId);
    };
    Object.keys(SSE_FRAME_HANDLERS).forEach((eventName) => {
        source.addEventListener(eventName, (event) => {
            if (!active || !(event instanceof MessageEvent)) return;
            rememberEventId(event);
            applySseFrame(AlpineInstance, {
                event: event.type,
                data: String(event.data),
            });
        });
    });
    source.addEventListener('stream-open', () => {
        if (!active) return;
        lifecycle.open?.();
    });
    source.addEventListener('stream-reset', (event) => {
        if (!active) return;
        if (event instanceof MessageEvent) rememberEventId(event);
        lifecycle.reset?.(event instanceof MessageEvent ? String(event.data) : '');
    });
    source.addEventListener('stream-error', (event) => {
        if (!active) return;
        active = false;
        source.close();
        lifecycle.error?.(event instanceof MessageEvent ? String(event.data) : '');
    });
    source.addEventListener('error', () => {
        if (!active) return;
        active = false;
        source.close();
        lifecycle.disconnected?.();
    });

    return {
        abort() {
            active = false;
            source.close();
        },
    };
};

type StreamControllerKey = Element | string;

type ManagedStream = {
    configuration: string;
    controller: StreamController | null;
    element: HTMLElement;
    dispose(): void;
    rescheduleRefresh(): void;
};

const streamControllers = new Map<StreamControllerKey, ManagedStream>();
const pendingStreamReconciliations = new Set<StreamControllerKey>();

const streamControllerKey = (el: Element): StreamControllerKey => (
    el instanceof HTMLElement && el.id ? el.id : el
);

type StreamLifecycleName = 'connecting' | 'open' | 'disconnected' | 'suspended' | 'reset' | 'error';
type ClientSessionRefresh = {token: string; expiresAt: number};

const CLIENT_SESSION_REFRESH_LEAD_MS = 60_000;
const STREAM_IDLE_SUSPEND_DELAY_MS = 1_000;
const STREAM_MUTATION_DELIVERY_TAIL_MS = 2_000;
const STREAM_RETRY_BASE_MS = 1_000;
const STREAM_RETRY_MAX_MS = 30_000;

const dispatchStreamLifecycle = (
    el: HTMLElement,
    name: StreamLifecycleName,
    detail: Record<string, unknown> = {},
): void => {
    if (name !== 'reset') el.dataset.uiStreamState = name;
    el.dispatchEvent(new CustomEvent(`ui:stream:${name}`, {
        bubbles: true,
        composed: true,
        detail: {source: el, ...detail},
    }));
};

const configuredStreamUrl = (el: HTMLElement, expression: string): string => (
    streamUrl(expression, el.dataset.uiStreamScopes, el.dataset.uiStreamClient)
);

const streamOpenWhenHidden = (el: HTMLElement): boolean => (
    el.dataset.uiStreamOpenWhenHidden === 'true'
);

const configuredStream = (el: HTMLElement, expression: string): string => (
    JSON.stringify({
        openWhenHidden: streamOpenWhenHidden(el),
        url: configuredStreamUrl(el, expression),
    })
);

const requestLifecycleDetail = (event: Event): RequestLifecycleDetail | null => {
    if (!(event instanceof CustomEvent)) return null;
    const detail = event.detail as Partial<RequestLifecycleDetail> | null;
    if (
        !detail
        || typeof detail.requestId !== 'string'
        || typeof detail.method !== 'string'
        || !(detail.controller instanceof AbortController)
    ) return null;
    return detail as RequestLifecycleDetail;
};

const createManagedStream = (
    AlpineInstance: Alpine.Alpine,
    el: HTMLElement,
    expression: string,
): ManagedStream => {
    let configuration = configuredStream(el, expression);
    let controller: StreamController | null = null;
    let refreshTimer: number | undefined;
    let refreshPromise: Promise<string | null> | null = null;
    let reconnectTimer: number | undefined;
    let suspendTimer: number | undefined;
    let connectedOnce = false;
    let lastEventId = '';
    let connectionGeneration = 0;
    let reconnectAttempts = 0;
    let terminal = false;
    let disposed = false;
    const activeGets = new Map<string, AbortController>();
    const activeMutations = new Set<string>();
    const openWhenHidden = streamOpenWhenHidden(el);

    const shouldBeConnected = (): boolean => (
        !terminal && (openWhenHidden || document.visibilityState === 'visible')
    );

    const clearReconnectTimer = (): void => {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
    };

    const dispatchResumed = (): void => {
        el.dispatchEvent(new CustomEvent('ui:stream:resumed', {
            bubbles: true,
            composed: true,
        }));
    };

    const connect = (token: string): void => {
        controller?.abort();
        clearReconnectTimer();
        const generation = ++connectionGeneration;
        configuration = configuredStream(el, expression);
        dispatchStreamLifecycle(el, 'connecting');
        controller = connectStream(
            AlpineInstance,
            expression,
            el.dataset.uiStreamScopes,
            token,
            {
                lastEventId,
                open: () => {
                    if (generation !== connectionGeneration) return;
                    const resumed = connectedOnce;
                    connectedOnce = true;
                    reconnectAttempts = 0;
                    dispatchStreamLifecycle(el, 'open');
                    if (resumed) dispatchResumed();
                },
                disconnected: () => {
                    if (generation !== connectionGeneration || disposed) return;
                    controller = null;
                    managed.controller = null;
                    dispatchStreamLifecycle(el, 'disconnected');
                    if (!shouldBeConnected()) return;
                    const ceiling = Math.min(
                        STREAM_RETRY_BASE_MS * (2 ** reconnectAttempts),
                        STREAM_RETRY_MAX_MS,
                    );
                    reconnectAttempts += 1;
                    const delay = Math.floor(Math.random() * ceiling);
                    reconnectTimer = window.setTimeout(() => {
                        reconnectTimer = undefined;
                        void ensureConnected();
                    }, delay);
                },
                reset: (data) => dispatchStreamLifecycle(el, 'reset', {data}),
                eventId: (value) => {
                    if (generation === connectionGeneration) lastEventId = value;
                },
                error: (data) => {
                    if (generation !== connectionGeneration || disposed) return;
                    terminal = true;
                    controller = null;
                    managed.controller = null;
                    clearReconnectTimer();
                    dispatchStreamLifecycle(el, 'error', {data});
                },
            },
        );
        managed.controller = controller;
        managed.configuration = configuration;
        scheduleRefresh();
    };

    const scheduleRefresh = (): void => {
        window.clearTimeout(refreshTimer);
        refreshTimer = undefined;
        if (!shouldBeConnected()) return;
        const expiresAt = Number(el.dataset.uiStreamClientExpires);
        if (!Number.isFinite(expiresAt) || expiresAt <= 0) return;
        const delay = expiresAt - Date.now() - CLIENT_SESSION_REFRESH_LEAD_MS;
        if (delay <= 0) {
            void rotateCapability();
            return;
        }
        refreshTimer = window.setTimeout(() => void rotateCapability(), delay);
    };

    const refreshCapability = (): Promise<string | null> => {
        if (disposed) return Promise.resolve(null);
        if (refreshPromise) return refreshPromise;
        const refreshUrl = el.dataset.uiStreamClientRefresh;
        if (!refreshUrl) {
            dispatchStreamLifecycle(el, 'error', {reason: 'missing-refresh-url'});
            return Promise.resolve(null);
        }

        refreshPromise = fetch(refreshUrl, {
            headers: {
                Accept: 'application/json',
                'X-Trellis-Client': el.dataset.uiStreamClient ?? '',
            },
        }).then(async (response) => {
            if (!response.ok) throw new Error(`ClientSession refresh failed (${response.status})`);
            const payload = await response.json() as Partial<ClientSessionRefresh>;
            if (typeof payload.token !== 'string' || typeof payload.expiresAt !== 'number') {
                throw new Error('ClientSession refresh returned an invalid payload');
            }
            if (disposed) return null;
            el.dataset.uiStreamClient = payload.token;
            el.dataset.uiStreamClientExpires = String(payload.expiresAt);
            return payload.token;
        }).catch((error: unknown) => {
            warn('x-ui-stream: could not refresh ClientSession', error);
            const expiresAt = Number(el.dataset.uiStreamClientExpires);
            if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
                dispatchStreamLifecycle(el, 'error', {error});
                return null;
            }
            if (shouldBeConnected()) {
                refreshTimer = window.setTimeout(() => void rotateCapability(), 5000);
            }
            return el.dataset.uiStreamClient ?? null;
        }).finally(() => {
            refreshPromise = null;
        });
        return refreshPromise;
    };

    const rotateCapability = async (): Promise<void> => {
        if (!shouldBeConnected()) return;
        const token = await refreshCapability();
        if (token && shouldBeConnected() && !disposed) connect(token);
    };

    const ensureConnected = async (): Promise<void> => {
        if (disposed || !shouldBeConnected() || controller) return;
        const expiresAt = Number(el.dataset.uiStreamClientExpires);
        const currentToken = el.dataset.uiStreamClient ?? '';
        const needsRefresh = Number.isFinite(expiresAt)
            && expiresAt > 0
            && expiresAt - Date.now() <= CLIENT_SESSION_REFRESH_LEAD_MS;
        const token = needsRefresh
            ? await refreshCapability()
            : currentToken;
        if (token && shouldBeConnected() && !disposed && !controller) connect(token);
    };

    const suspend = (): void => {
        suspendTimer = undefined;
        if (disposed || openWhenHidden || document.visibilityState !== 'hidden') return;
        if (activeMutations.size > 0) return;
        activeGets.forEach((requestController) => requestController.abort('Document suspended'));
        activeGets.clear();
        window.clearTimeout(refreshTimer);
        refreshTimer = undefined;
        clearReconnectTimer();
        controller?.abort();
        controller = null;
        dispatchStreamLifecycle(el, 'suspended');
        managed.controller = null;
    };

    const scheduleSuspend = (delay: number): void => {
        window.clearTimeout(suspendTimer);
        suspendTimer = undefined;
        if (openWhenHidden || document.visibilityState !== 'hidden') return;
        suspendTimer = window.setTimeout(suspend, delay);
    };

    const onRefresh = (): void => {
        if (terminal) return;
        void rotateCapability();
    };

    const onRequestStart = (event: Event): void => {
        const detail = requestLifecycleDetail(event);
        if (!detail) return;
        if (detail.method === 'GET') {
            activeGets.set(detail.requestId, detail.controller);
            return;
        }
        activeMutations.add(detail.requestId);
        if (document.visibilityState === 'hidden' && !openWhenHidden) {
            window.clearTimeout(suspendTimer);
            suspendTimer = undefined;
        }
    };

    const onRequestFinish = (event: Event): void => {
        const detail = requestLifecycleDetail(event);
        if (!detail) return;
        if (detail.method === 'GET') {
            activeGets.delete(detail.requestId);
            return;
        }
        activeMutations.delete(detail.requestId);
        if (document.visibilityState === 'hidden' && !openWhenHidden) {
            if (activeMutations.size === 0) {
                scheduleSuspend(STREAM_MUTATION_DELIVERY_TAIL_MS);
            }
        }
    };

    const onVisibilityChange = (): void => {
        if (openWhenHidden || terminal) return;
        if (document.visibilityState === 'hidden') {
            if (activeMutations.size > 0) {
                return;
            }
            scheduleSuspend(STREAM_IDLE_SUSPEND_DELAY_MS);
            return;
        }
        window.clearTimeout(suspendTimer);
        suspendTimer = undefined;
        if (!controller) void ensureConnected();
    };

    const managed: ManagedStream = {
        configuration,
        controller: null,
        element: el,
        dispose(): void {
            if (disposed) return;
            disposed = true;
            window.clearTimeout(refreshTimer);
            window.clearTimeout(suspendTimer);
            clearReconnectTimer();
            el.removeEventListener('ui:stream:refresh', onRefresh);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            document.removeEventListener('ui:request:start', onRequestStart);
            document.removeEventListener('ui:request:finish', onRequestFinish);
            controller?.abort();
        },
        rescheduleRefresh: scheduleRefresh,
    };

    declareMorphAttributes(el, 'data-ui-stream-state');
    el.addEventListener('ui:stream:refresh', onRefresh);
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('ui:request:start', onRequestStart);
    document.addEventListener('ui:request:finish', onRequestFinish);
    if (shouldBeConnected()) {
        void ensureConnected();
    } else {
        dispatchStreamLifecycle(el, 'suspended');
    }
    return managed;
};

const registerStream = (AlpineInstance: Alpine.Alpine): void => {
    if (typeof AlpineInstance.directive !== 'function') return;

    AlpineInstance.directive('ui-stream', (el, {expression}, {cleanup}) => {
        if (!expression) return;

        const client = el instanceof HTMLElement ? el.dataset.uiStreamClient : '';
        if (!(el instanceof HTMLElement) || !client) {
            warn('x-ui-stream: missing signed ClientSession capability');
            return;
        }
        const key = streamControllerKey(el);
        const existing = streamControllers.get(key);
        const configuration = configuredStream(el, expression);
        if (existing?.configuration === configuration) {
            el.dataset.uiStreamState = existing.element.dataset.uiStreamState ?? 'connecting';
            declareMorphAttributes(el, 'data-ui-stream-state');
            queueMicrotask(() => existing.rescheduleRefresh());
            return;
        }

        if (!el.isConnected && typeof key === 'string') {
            if (!pendingStreamReconciliations.has(key)) {
                pendingStreamReconciliations.add(key);
                queueMicrotask(() => {
                    pendingStreamReconciliations.delete(key);
                    const live = document.getElementById(key);
                    if (!live) return;
                    const current = streamControllers.get(key);
                    const liveConfiguration = configuredStream(live, expression);
                    if (current?.configuration === liveConfiguration) {
                        current.rescheduleRefresh();
                        return;
                    }
                    current?.dispose();
                    streamControllers.set(key, createManagedStream(AlpineInstance, live, expression));
                });
            }
            return;
        }

        existing?.dispose();
        streamControllers.set(key, createManagedStream(AlpineInstance, el, expression));

        cleanup(() => {
            const current = streamControllers.get(key);
            if (current?.element !== el) return;
            current.dispose();
            streamControllers.delete(key);
        });
    });
};

const setEffectRequestFilter = (filter: ((requestId: string | null) => boolean) | null): void => {
    effectRequestFilter = filter;
};

const setMorphExclusionFilter = (
    filter: ((requestId: string | null, element: Element) => boolean) | null,
): void => {
    morphExclusionFilter = filter;
};

export {
    applyUiEffect,
    applyUiEffects,
    applyStreamPatch,
    applyStreamPatchNow,
    applyStreamPatches,
    applySseFrame,
    connectStream,
    dispatchStreamEvent,
    registerStream,
    registerStreamPatchAnimator,
    setEffectRequestFilter,
    setMorphExclusionFilter,
    streamUrl,
};
export type {
    PatchMode,
    SseFrame,
    StreamAlpine,
    StreamEventPayload,
    StreamPatchAnimator,
    StreamPatchPayload,
    UiEffectPayload,
};
